import {Component, inject, signal, WritableSignal} from '@angular/core';
import {ModalRef} from '@shared/components/modal/modal.ref';
import {FormsModule} from '@angular/forms';
import {PappersService} from '@services/pappers.service';
import {ICompanySearchSuggestion, IPappersDetailedInformations} from '@models/pappers.interface';
import {Check, LucideAngularModule, Plus, Search} from '@shared/components/icons/google-icons';
import {CompanyService} from '@services/company.service';
import {NotificationService} from '@services/frontend/notification.service';
import {ICompteAdministration} from '@models/compteAdministration.interface';
import {Router} from '@angular/router';
import {MODAL_CONFIG} from '@shared/components/modal/modal.token';
import {ModalConfig} from '@shared/components/modal/modal.config';
import {CompanyRefreshService} from '@services/company-refresh.service';

@Component({
  selector: 'app-company-add-dialog',
  imports: [
    FormsModule,
    LucideAngularModule
  ],
  templateUrl: './company-add-dialog.html'
})
export class CompanyAddDialog {

  private notificationService = inject(NotificationService);
  private companyService = inject(CompanyService);
  private pappersService = inject(PappersService);
  private modalRef = inject(ModalRef);
  private router = inject(Router);
  private companyRefreshService = inject(CompanyRefreshService);
  private readonly config = inject<ModalConfig | null>(MODAL_CONFIG, {optional: true});

  proUpgradeRequired = this.config?.data?.proUpgradeRequired === true;

  companyAdded: WritableSignal<boolean> = signal(false);
  query: WritableSignal<string> = signal('');
  selectedSiret: WritableSignal<string> = signal('');
  loading: WritableSignal<boolean>  = signal(false);
  searching: WritableSignal<boolean>  = signal(false);
  showSuggestions: WritableSignal<boolean>  = signal(false);
  error: WritableSignal<string | null>  = signal<string | null>(null);
  company: WritableSignal<IPappersDetailedInformations | null>  = signal<IPappersDetailedInformations | null>(null);
  suggestions: WritableSignal<ICompanySearchSuggestion[]> = signal([]);
  private searchTimeout?: number;

  onQueryInput(value: string): void {
    this.query.set(value);
    this.company.set(null);
    this.companyAdded.set(false);
    this.selectedSiret.set('');
    this.error.set(null);
    clearTimeout(this.searchTimeout);

    if (value.trim().length < 2) {
      this.suggestions.set([]);
      this.showSuggestions.set(false);
      return;
    }

    this.searchTimeout = window.setTimeout(() => {
      this.searching.set(true);
      this.pappersService.searchCompanies(value.trim(), false).subscribe({
        next: (results) => {
          this.searching.set(false);
          this.suggestions.set(results);
          this.showSuggestions.set(true);
        },
        error: () => {
          this.searching.set(false);
          this.suggestions.set([]);
          this.showSuggestions.set(true);
        }
      });
    }, 300);
  }

  selectSuggestion(suggestion: ICompanySearchSuggestion): void {
    const siret = suggestion.siret;
    if (!siret) {
      this.error.set('SIRET indisponible pour cette entreprise');
      return;
    }
    this.query.set(suggestion.companyName || siret);
    this.selectedSiret.set(siret);
    this.showSuggestions.set(false);
    this.searchBySiret(siret);
  }

  searchBySiret(siret: string) {
    const value = siret.trim();

    if (!value || value.length !== 14) {
      this.error.set('Le SIRET doit contenir 14 chiffres');
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.company.set(null);
    this.selectedSiret.set(value);

    this.pappersService.findCompanyBySiret(value)
      .subscribe({
        next: (res: IPappersDetailedInformations)=> {
          this.company.set(res);
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Entreprise introuvable');
          this.loading.set(false);
        }
      });
  }

  addCompany() {
    const siret = this.selectedSiret();
    if (!siret) {
      this.error.set('Sélectionnez une entreprise dans les suggestions');
      return;
    }

    this.companyService.createCompanyWithoutExternalAccount(siret).subscribe({
      next: (value: ICompteAdministration) => {
        this.notificationService.success(`Ajout avec succès de l'entreprise ${value.company.companyDetailResponse.nomEntreprise}`, 2000);
        this.companyAdded.set(true);
        this.companyRefreshService.requestRefresh();
        setTimeout(() => {
          this.modalRef.close(true);
          void this.router.navigate(['/dashboard', 'company', siret, 'tableau-de-bord']);
        }, 500);
      },
      error: err => this.error.set(err)
    });
  }

  protected readonly Search = Search;
  protected readonly Plus = Plus;
  protected readonly Check = Check;
}
